import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Rocket, ArrowRight, CircleNotch, CheckCircle, MagnifyingGlass, Sparkle,
  Target, Fire, Confetti, ArrowLeft, X, Lightning, Crown,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NICHE_PRESETS = [
  { key: 'coaching', label: 'Coaching & Consulting', icon: '🎯' },
  { key: 'ecommerce', label: 'E-commerce', icon: '🛍️' },
  { key: 'saas', label: 'SaaS & Software', icon: '⚡' },
  { key: 'infoproducts', label: 'Info products & Courses', icon: '📚' },
  { key: 'agency', label: 'Agency', icon: '🏢' },
  { key: 'health', label: 'Health & Fitness', icon: '💪' },
  { key: 'finance', label: 'Finance & Trading', icon: '💰' },
  { key: 'other', label: 'Something else', icon: '✨' },
];

/**
 * 4-step first-run wizard.
 * Purpose: raise activation rate — new users see value in <60s.
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [variant, setVariant] = useState('control');
  const [niche, setNiche] = useState('');
  const [customNiche, setCustomNiche] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [heatmap, setHeatmap] = useState(null);
  const [copy, setCopy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  // On mount: fetch progress → resume where user left off (if not yet completed)
  useEffect(() => {
    (async () => {
      let v = 'control';
      try {
        const exp = await axios.get(`${BACKEND_URL}/api/experiments/assign/onboarding_v1`, { withCredentials: true });
        v = exp.data.variant || 'control';
        setVariant(v);
      } catch { /* default control */ }
      try {
        const res = await axios.get(`${BACKEND_URL}/api/onboarding/status`, { withCredentials: true });
        if (res.data.completed) { navigate('/dashboard'); return; }
        if (res.data.niche) setNiche(res.data.niche);
        if (res.data.website_url) setWebsiteUrl(res.data.website_url);
        const saved = res.data.current_step || 1;
        // fast_lane variant skips the niche step — fresh users start at the URL step
        setStep(v === 'fast_lane' && saved <= 1 ? 2 : saved);
      } catch { /* first-time user, defaults fine */
        if (v === 'fast_lane') setStep(2);
      }
      setChecking(false);
    })();
  }, [navigate]);

  const saveProgress = useCallback(async (patch) => {
    try {
      await axios.post(`${BACKEND_URL}/api/onboarding/progress`, { step, ...patch }, { withCredentials: true });
    } catch { /* non-blocking */ }
  }, [step]);

  // ── Step 2 → run heatmap ─────────────────────────────────────
  const runHeatmap = useCallback(async () => {
    const url = websiteUrl.trim();
    if (!url) { toast.error(t('onbw.err_enter_url')); return; }
    setBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/heatmap/analyze`,
        { url },
        { withCredentials: true, timeout: 60000 },
      );
      setHeatmap(res.data);
      await saveProgress({ step: 3, website_url: url, heatmap_id: res.data.heatmap_id });
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('onbw.err_analysis'));
    } finally {
      setBusy(false);
    }
  }, [websiteUrl, saveProgress, t]);

  // ── Step 3 → generate headlines from heatmap context ─────────
  const runCopy = useCallback(async () => {
    if (!heatmap) return;
    setBusy(true);
    try {
      const nicheLabel = niche === 'other' ? customNiche : (NICHE_PRESETS.find(n => n.key === niche)?.label || niche);
      const context = [
        `Niche: ${nicheLabel || 'general'}`,
        `Website: ${heatmap.url}`,
        heatmap.title ? `Page title: ${heatmap.title}` : '',
        heatmap.insights?.summary ? `Key insight: ${heatmap.insights.summary}` : '',
      ].filter(Boolean).join('\n');
      const res = await axios.post(
        `${BACKEND_URL}/api/ai/generate`,
        {
          content_type: 'headline',
          prompt: `Write 3 high-converting alternative headlines for this page. Return them as a numbered list. Make each one distinct in angle (curiosity, benefit-driven, contrarian).`,
          context,
        },
        { withCredentials: true, timeout: 60000 },
      );
      setCopy(res.data);
      await saveProgress({ step: 4, content_id: res.data.content_id });
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('onbw.err_copy'));
    } finally {
      setBusy(false);
    }
  }, [heatmap, niche, customNiche, saveProgress, t]);

  // ── Finalise ─────────────────────────────────────────────────
  const finish = useCallback(async (skipped = false) => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/onboarding/complete`, { skipped }, { withCredentials: true });
      if (!skipped) {
        // Fire A/B conversion (fire-and-forget; skips don't count as goal reached)
        axios.post(`${BACKEND_URL}/api/experiments/convert`, { experiment_id: 'onboarding_v1' }, { withCredentials: true }).catch(() => {});
        toast.success(t('onbw.all_set'));
      }
      navigate('/dashboard');
    } catch {
      navigate('/dashboard');  // fail-open — don't trap the user
    }
  }, [navigate, t]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <CircleNotch size={40} className="text-[#A855F7] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Header — progress bar */}
      <header className="px-6 sm:px-10 py-5 border-b border-white/10">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-[Outfit] font-bold">
            <Rocket size={22} weight="fill" className="text-[#A855F7]" />
            <span>{t('onbw.setup')}</span>
          </div>
          <button
            onClick={() => finish(true)}
            data-testid="onboarding-skip"
            className="text-xs text-white/40 hover:text-white/70 flex items-center gap-1"
          >
            {t('onbw.skip')} <X size={14} />
          </button>
        </div>
        <div className="max-w-3xl mx-auto mt-4">
          <div className="flex items-center gap-2" data-testid={`onboarding-progress-${variant}`}>
            {(variant === 'fast_lane' ? [2, 3, 4] : [1, 2, 3, 4]).map((n, i, arr) => (
              <div key={n} className="flex-1 flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                    step > n ? 'bg-[#10B981] text-black' :
                    step === n ? 'bg-[#A855F7] text-white ring-4 ring-[#A855F7]/30' :
                    'bg-white/10 text-white/40'
                  }`}
                >
                  {step > n ? <CheckCircle size={14} weight="fill" /> : i + 1}
                </div>
                {i < arr.length - 1 && (
                  <div className="flex-1 h-0.5 bg-white/10 relative overflow-hidden rounded-full">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#10B981] to-[#A855F7] transition-all"
                      style={{ width: step > n ? '100%' : '0%' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-6 sm:px-10 py-10 sm:py-16">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <StepShell key="1" title={t('onbw.s1_title')} subtitle={t('onbw.s1_sub')}>
                <div className="grid grid-cols-2 gap-3" data-testid="onboarding-niches">
                  {NICHE_PRESETS.map((n) => (
                    <button
                      key={n.key}
                      onClick={() => setNiche(n.key)}
                      data-testid={`niche-${n.key}`}
                      className={`p-4 rounded-xl text-left flex items-center gap-3 border-2 transition-all ${
                        niche === n.key
                          ? 'bg-[#A855F7]/10 border-[#A855F7] shadow-[0_0_30px_-10px_rgba(168,85,247,0.5)]'
                          : 'bg-white/5 border-white/10 hover:border-white/30 hover:bg-white/10'
                      }`}
                    >
                      <span className="text-2xl">{n.icon}</span>
                      <span className="text-sm font-medium">{t(`onbw.niche_${n.key}`)}</span>
                    </button>
                  ))}
                </div>
                {niche === 'other' && (
                  <input
                    autoFocus
                    value={customNiche}
                    onChange={(e) => setCustomNiche(e.target.value)}
                    placeholder={t('onbw.custom_niche_ph')}
                    maxLength={120}
                    data-testid="niche-custom"
                    className="mt-4 w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-[#A855F7]"
                  />
                )}
                <StepFooter
                  onNext={async () => {
                    if (!niche) { toast.error('Pick a niche to continue'); return; }
                    if (niche === 'other' && !customNiche.trim()) { toast.error('Describe your niche'); return; }
                    const value = niche === 'other' ? customNiche.trim() : niche;
                    await saveProgress({ step: 2, niche: value });
                    setStep(2);
                  }}
                  nextLabel={t('onbw.next')}
                  hideBack
                />
              </StepShell>
            )}

            {step === 2 && (
              <StepShell key="2"
                title={variant === 'fast_lane' ? t('onbw.s2_title_fast') : t('onbw.s2_title')}
                subtitle={t('onbw.s2_sub')}
              >
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus-within:border-[#A855F7]">
                  <MagnifyingGlass size={18} className="text-white/40" />
                  <input
                    autoFocus
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !busy && runHeatmap()}
                    placeholder="https://your-landing-page.com"
                    data-testid="onboarding-url-input"
                    className="flex-1 bg-transparent outline-none text-sm"
                  />
                </div>
                <ul className="mt-4 text-xs text-white/50 space-y-1.5">
                  <li className="flex items-center gap-2"><CheckCircle size={12} className="text-[#10B981]" /> {t('onbw.s2_feat1')}</li>
                  <li className="flex items-center gap-2"><CheckCircle size={12} className="text-[#10B981]" /> {t('onbw.s2_feat2')}</li>
                  <li className="flex items-center gap-2"><CheckCircle size={12} className="text-[#10B981]" /> {t('onbw.s2_feat3')}</li>
                </ul>
                <StepFooter
                  onBack={() => setStep(1)}
                  onNext={runHeatmap}
                  nextLabel={busy ? t('onbw.analysing') : t('onbw.run_heatmap')}
                  nextIcon={busy ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
                  busy={busy}
                  hideBack={variant === 'fast_lane'}
                />
              </StepShell>
            )}

            {step === 3 && heatmap && (
              <StepShell key="3"
                title={t('onbw.s3_title')}
                subtitle={t('onbw.s3_sub')}
              >
                {(heatmap.score != null) && (
                  <div className="p-5 rounded-xl bg-gradient-to-br from-[#10B981]/10 to-[#2563EB]/10 border border-[#10B981]/30 mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-widest text-[#10B981]">{t('onbw.score')}</span>
                      <span className="text-4xl font-bold font-[Outfit]" data-testid="onboarding-heatmap-score">{heatmap.score}<span className="text-lg text-white/40">/100</span></span>
                    </div>
                    <div className="text-sm text-white/70 truncate">{heatmap.url}</div>
                    {heatmap.insights?.summary && (
                      <div className="mt-3 text-sm text-white/80">{heatmap.insights.summary}</div>
                    )}
                  </div>
                )}
                {(heatmap.score == null) && heatmap.insights?.summary && (
                  <div className="p-5 rounded-xl bg-gradient-to-br from-[#10B981]/10 to-[#2563EB]/10 border border-[#10B981]/30 mb-5" data-testid="onboarding-heatmap-insight">
                    <div className="text-xs uppercase tracking-widest text-[#10B981] mb-2">{t('onbw.insight')}</div>
                    <div className="text-sm text-white/80 truncate mb-2">{heatmap.url}</div>
                    <div className="text-sm text-white/90">{heatmap.insights.summary}</div>
                  </div>
                )}
                <div className="p-4 rounded-xl bg-[#A855F7]/5 border border-[#A855F7]/20 flex items-start gap-3">
                  <Sparkle size={20} weight="fill" className="text-[#A855F7] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">{t('onbw.gen_box_title')}</div>
                    <div className="text-xs text-white/60 mt-1">
                      {t('onbw.gen_box_sub')}
                    </div>
                  </div>
                </div>
                <StepFooter
                  onBack={() => setStep(2)}
                  onNext={runCopy}
                  nextLabel={busy ? t('onbw.writing') : t('onbw.generate_copy')}
                  nextIcon={busy ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
                  busy={busy}
                />
              </StepShell>
            )}

            {step === 4 && (
              <StepShell key="4"
                title={t('onbw.s4_title')}
                subtitle={t('onbw.s4_sub')}
              >
                {copy && (
                  <div className="p-5 rounded-xl bg-white/5 border border-white/10 mb-5" data-testid="onboarding-copy-output">
                    <div className="text-xs uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
                      <Sparkle size={12} weight="fill" className="text-[#A855F7]" /> {t('onbw.your_headlines')}
                    </div>
                    <div className="text-sm text-white/90 whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto">
                      {copy.generated_content}
                    </div>
                  </div>
                )}

                {/* Contextual upsell */}
                <div className="p-5 rounded-2xl bg-gradient-to-br from-[#A855F7]/15 via-[#2563EB]/10 to-[#F59E0B]/10 border border-[#A855F7]/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown size={18} weight="fill" className="text-[#F59E0B]" />
                    <span className="text-xs uppercase tracking-widest text-[#F59E0B]">{t('onbw.upsell_kicker')}</span>
                  </div>
                  <div className="text-lg font-[Outfit] font-bold mb-2">{t('onbw.upsell_title')}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
                    {[
                      { icon: <Fire size={12} weight="fill" className="text-[#EF4444]" />, label: t('onbw.f_heatmaps') },
                      { icon: <Target size={12} weight="fill" className="text-[#F59E0B]" />, label: t('onbw.f_spy') },
                      { icon: <Sparkle size={12} weight="fill" className="text-[#A855F7]" />, label: t('onbw.f_copy') },
                      { icon: <Rocket size={12} weight="fill" className="text-[#2563EB]" />, label: t('onbw.f_autobuild') },
                      { icon: <Confetti size={12} weight="fill" className="text-[#10B981]" />, label: t('onbw.f_crm') },
                      { icon: <Lightning size={12} weight="fill" className="text-[#EC4899]" />, label: t('onbw.f_battle') },
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-white/70">
                        {f.icon} {f.label}
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => finish(false).then(() => navigate('/pricing'))}
                      data-testid="onboarding-upgrade"
                      className="flex-1 px-5 py-3 rounded-lg bg-[#A855F7] hover:bg-[#9333EA] font-semibold flex items-center justify-center gap-2 transition-all"
                    >
                      <Crown size={16} weight="fill" /> {t('onbw.upgrade')}
                    </button>
                    <button
                      onClick={() => finish(false)}
                      data-testid="onboarding-continue-free"
                      className="flex-1 px-5 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm flex items-center justify-center gap-2"
                    >
                      {t('onbw.explore_free')} <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </StepShell>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

// ─────────────────────────────── Sub-components ───────────────────────

function StepShell({ title, subtitle, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      <h1 className="text-3xl sm:text-4xl font-[Outfit] font-bold mb-3">{title}</h1>
      <p className="text-white/60 mb-8">{subtitle}</p>
      {children}
    </motion.div>
  );
}

function StepFooter({ onBack, onNext, nextLabel, nextIcon, busy, hideBack }) {
  const { t } = useTranslation();
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {!hideBack ? (
        <button
          onClick={onBack}
          data-testid="onboarding-back"
          className="px-4 py-2 text-white/60 hover:text-white flex items-center gap-2 text-sm"
        >
          <ArrowLeft size={14} /> {t('onbw.back')}
        </button>
      ) : <span />}
      <button
        onClick={onNext}
        disabled={busy}
        data-testid="onboarding-next"
        className="px-6 py-3 rounded-lg bg-[#A855F7] hover:bg-[#9333EA] disabled:opacity-60 disabled:cursor-not-allowed font-medium flex items-center gap-2 transition-all"
      >
        {nextLabel || t('onbw.next')} {nextIcon || <ArrowRight size={16} />}
      </button>
    </div>
  );
}

